import { z } from "zod";
import { expenseViewerActionsSchema, type ExpenseViewerActions } from "./expenses.js";

/* ===========================================================================
 * US-E7.F1-0079 Activity Feed & Action Items (@UC0074) — Engagement (Phase 8.5, read-only).
 *
 *  - GET /v1/groups/:id/activity — feed hoạt động nhóm (projection READ-ONLY từ bảng nguồn sự thật:
 *    Collection/Expense/Consensus/GroupMember), cursor keyset (createdAt,id desc), scope membership.
 *    Redaction fail-closed: Member thường + fund transparency=private → OMIT `amountMinor` (ẩn số tiền).
 *  - GET /v1/me/action-items — badge việc cần làm: đếm dues unpaid/overdue + vote đang mở chưa bỏ +
 *    pay-on-behalf chờ tôi respond. Chỉ của tôi (IDOR — userId từ token).
 *
 * Tiền STRING minor unit (B1, no float). KHÔNG post/update ledger — chỉ SELECT.
 * ========================================================================= */

const MONEY_INTEGER_STRING_PATTERN = /^-?\d+$/;

/**
 * Loại sự kiện hiển thị trong feed (projection từ bảng nguồn — semantics khớp outbox eventType).
 *
 * 🔴 US-E24.F12-0361 (@UC9591): `PAYMENT_CONFIRMED` = **tiền VÀO ĐÃ XÁC NHẬN** (trạng thái 3 của
 * CLAUDE.md §3.7), projection từ `ledger_transactions` — bảng FACT append-only. Tên cố ý TRÙNG
 * eventType outbox đang có (`apps/worker/.../event-types.ts`), KHÔNG đặt tên mới.
 * ⚠️ Lời khai chưa duyệt KHÔNG BAO GIỜ là sự kiện này — đưa claim lên feed công khai của cả nhóm là
 * sổ ảo + bêu tên. Câu đó **vẫn đúng nguyên văn**; từ US-E6.F3-0599 lời khai có loại RIÊNG
 * (`PAYMENT_AWAITING_CONFIRMATION`) với **khán giả hẹp**, chứ không được nhập vào loại này.
 *
 * 🔴 Whitelist này khai HAI LẦN (object hằng + `z.enum` dưới). Thêm giá trị phải sửa CẢ HAI —
 * guard tự-khám-phá `activity-event-type.test.ts` so `schema.options` với `Object.values(...)`.
 */
export const ACTIVITY_EVENT_TYPE = {
  COLLECTION_CREATED: "collection.created",
  EXPENSE_APPROVED: "expense.approved",
  EXPENSE_REJECTED: "expense.rejected",
  MEMBER_JOINED: "member.joined",
  /**
   * 🔴 US-E24.F50-0502 (@UC0074): NGƯỜI RỜI NHÓM. Feed cũ chỉ có `member.joined` ⇒ sĩ số trên feed
   * chỉ tăng, không bao giờ giảm, và nhóm không đối chiếu được vì sao kỳ này ít người đóng hơn kỳ
   * trước. Projection từ **`group_members.leftAt`** — cột đã có sẵn, khai đúng nghĩa "MỐC RỜI"
   * (US-E19.F3-0202). KHÔNG suy từ `status`: `join_pending` vừa là "chờ được duyệt vào" vừa là
   * "đã rời, nằm hàng đợi" (QĐ PO 13/08, US-E4.F2-0396) ⇒ suy từ đó sẽ dựng dòng *"X đã rời nhóm"*
   * cho người mới xin vào. 0 migration, 0 bảng mới.
   */
  MEMBER_LEFT: "member.left",
  PAYMENT_CONFIRMED: "payment.confirmed",
  /**
   * 🔴 US-E6.F3-0599 (@UC0051 · @UC9593) — LỜI KHAI *"tôi đã chuyển"* ĐANG CHỜ NGƯỜI DUYỆT.
   *
   * Projection từ `payment_confirmations(status='submitted')` — **KHÔNG** từ `ledger_*`, vì chưa có
   * bút toán nào. Đây là **trạng thái (2)** của `CLAUDE.md §3.7` (*"đã chuyển – chưa đối chiếu"*),
   * và nó tồn tại trên feed vì trước 0599 **không màn nào của app** cho người có quyền duyệt biết là
   * có việc phải làm: cả `approve`/`decline` lẫn một service đọc hàng chờ (lane AI v1, đã xoá 27/09)
   * đều đã có sẵn nhưng **0 caller client**.
   *
   * 🔴 Tên trùng nguyên văn `payment_confirmations.status='submitted'` về ngữ nghĩa, và trùng
   * `member_dues.status='awaiting_confirmation'` về chữ ⇒ đọc dòng này là biết nó chiếu từ đâu.
   * KHÔNG đặt `payment.pending`: "pending" trong repo này đã là trạng thái *chưa đóng* của một due —
   * hai nghĩa cho một chữ là chỗ hiểu nhầm bắt đầu.
   *
   * 🔴 **KHÁN GIẢ** (QĐ người dùng 01/09/2026 QĐ-1, **nới 06/10/2026** — US-E7.F1-0911 E2): **người khai** và
   * **người có quyền duyệt** luôn thấy hàng này. Member khác thấy lời khai của người khác **chỉ ở quỹ MỞ**
   * (`transparency_mode=public` VÀ `payment_status_visibility=all_members` — cả hai mặc định), đủ tên + số; quỹ KÍN ⇒ **không
   * có hàng** (không phải "hàng đã ẩn tên" — QĐ-1 giữ nguyên cho nhóm kín). Luật lọc nằm ở NGUỒN của `ActivityFeedService`.
   */
  PAYMENT_AWAITING_CONFIRMATION: "payment.awaiting_confirmation",
  /**
   * 🔴 US-E6.F3-0912 (@UC0051 · QĐ người dùng 06/10/2026) — LỜI KHAI BỊ TỪ CHỐI. Projection từ `payment_confirmations`
   * `status='rejected'` mà người từ chối KHÁC người khai (`reviewedBy IS NOT NULL AND reviewedBy <> submittedBy`) — tự huỷ và
   * huỷ do sửa nhầm KHÔNG hiện. Mốc = `reviewedAt`. Không đồng nào vào quỹ ⇒ phân loại `neutral`. Lý do ở `rejectReason`.
   * Khán giả = khán giả hàng chờ duyệt (người duyệt · người khai · member ở quỹ MỞ).
   */
  PAYMENT_CLAIM_REJECTED: "payment.claim_rejected",
  /**
   * 🔴 US-E7.F1-0724 (@UC0074 · QĐ người dùng 07/09/2026) — ĐÓNG GÓP THÊM, NGOÀI nghĩa vụ
   * ("Đóng góp khác" trên app ⇒ `incomeType=sponsorship_member` ⇒ txType `SPONSORSHIP_MEMBER`).
   *
   * Bug người dùng báo: khoản này vào quỹ thật nhưng KHÔNG hiện ở tab Hoạt động. Đó không phải sót
   * mà là một quyết định cũ có chủ đích — `FEED_MONEY_IN_TX_TYPES` cố tình hẹp để câu *"X đã nộp
   * quỹ"* luôn đúng. QĐ 07/09 đảo phần "phải hiện", KHÔNG đảo phần "câu chữ phải đúng".
   *
   * 🔴 Vì thế là loại RIÊNG chứ không nhập vào `PAYMENT_CONFIRMED`: nó KHÔNG hoàn thành nghĩa vụ
   * nào (`memberDueId` null vĩnh viễn), nên nói "đã nộp quỹ" là kể một sự việc không xảy ra. Client
   * render câu riêng ("đã đóng góp thêm").
   *
   * 🔴 `actorName` có thể NULL kể cả khi mọi thứ chạy đúng: bút toán ghi TRƯỚC migration
   * `20260907120000` không có `sponsorMemberId` (hệ thống thật sự không lưu ai đã đóng góp), và
   * hàng sổ cái là append-only nên không backfill được. Client phải dựng được dòng KHÔNG TÊN —
   * đừng coi null là lỗi dữ liệu.
   */
  INCOME_RECORDED: "income.recorded",
  /**
   * 🔴 QĐ người dùng 12/10/2026 (@UC0074 @UC0052) — KHOẢN CHI ĐANG CHỜ QUYẾT (`expenses.status='pending_decision'`): yêu cầu chi
   * thành viên gửi, hoặc khoản chi quản lý ghi mà chưa vào sổ (cờ tự vào sổ tắt / nhóm biểu quyết). CHƯA đồng nào rời quỹ ⇒
   * `neutral`. Duyệt ⇒ thành `expense.approved` (cùng `id` = expenseId); từ chối ⇒ `expense.rejected`; người gửi huỷ ⇒ KHÔNG còn
   * dòng (như lời khai tự huỷ).
   *
   * 🔴 KHÁN GIẢ = khán giả của `payment.awaiting_confirmation`: người duyệt (O/A/T) + người gửi luôn thấy; member khác chỉ ở quỹ
   * MỞ; quỹ KÍN ⇒ không có dòng. Luật lọc ở NGUỒN (`where`). Hành động người xem làm được đọc ở `viewerActions` — SERVER tính.
   */
  EXPENSE_AWAITING_APPROVAL: "expense.awaiting_approval",
} as const;

export type ActivityEventType =
  (typeof ACTIVITY_EVENT_TYPE)[keyof typeof ACTIVITY_EVENT_TYPE];

export const activityEventTypeSchema = z.enum([
  ACTIVITY_EVENT_TYPE.COLLECTION_CREATED,
  ACTIVITY_EVENT_TYPE.EXPENSE_APPROVED,
  ACTIVITY_EVENT_TYPE.EXPENSE_REJECTED,
  ACTIVITY_EVENT_TYPE.MEMBER_JOINED,
  ACTIVITY_EVENT_TYPE.MEMBER_LEFT,
  ACTIVITY_EVENT_TYPE.PAYMENT_CONFIRMED,
  ACTIVITY_EVENT_TYPE.PAYMENT_AWAITING_CONFIRMATION,
  ACTIVITY_EVENT_TYPE.PAYMENT_CLAIM_REJECTED,
  ACTIVITY_EVENT_TYPE.INCOME_RECORDED,
  ACTIVITY_EVENT_TYPE.EXPENSE_AWAITING_APPROVAL,
]);

/**
 * Giá trị filter `?kind` của feed (US-E24.F12-0361 · @UC9593) — màn Figma B3 có chip **Thu / Chi**.
 *
 * 🔴 CỐ Ý KHÔNG có `all`: "Tất cả" = **không gửi param**. Một khái niệm, một cách biểu diễn
 * (mirror lý do repo cố ý không có enum `biweekly`) — thêm `all` là hai đường cho cùng một ý, rồi
 * sẽ có chỗ xử `all` còn chỗ xử `undefined`.
 */
export const ACTIVITY_KIND = {
  INCOME: "income",
  EXPENSE: "expense",
} as const;

export type ActivityKind = (typeof ACTIVITY_KIND)[keyof typeof ACTIVITY_KIND];

export const activityKindSchema = z.enum([ACTIVITY_KIND.INCOME, ACTIVITY_KIND.EXPENSE]);

/**
 * 🔴 PHÂN LOẠI THU / CHI / TRUNG TÍNH của từng eventType — khai **MỘT CHỖ**, server lọc và client
 * hiện chip đều đọc chung. `Record<ActivityEventType, …>` ⇒ thêm eventType mà quên phân loại là
 * **lỗi typecheck**, và guard runtime `activity-event-kind.test.ts` bắt thêm lần nữa.
 *
 * Vì sao có `neutral` chứ không nhét mọi thứ vào thu/chi:
 *  - `collection.created` = **NGHĨA VỤ** (nhóm quyết phải đóng) — chưa đồng nào vào quỹ. Xếp vào
 *    "thu" là đếm tiền chưa tồn tại (đúng thứ CLAUDE.md §3.7 tách bạch: nghĩa vụ ≠ đã nhận).
 *  - `expense.rejected` = **KHÔNG có tiền ra**. Xếp vào "chi" là hiện khoản chi không hề xảy ra.
 *  - `member.joined` / `member.left` = không dính tiền. Người rời nhóm mà còn nghĩa vụ đã bị chặn ở
 *    tầng khác (`hasNonZeroPosition`), nên dòng này KHÔNG BAO GIỜ là một khoản thu/chi.
 *
 * 🔴 Phạm vi là **BÁO CÁO NHÓM**, không phải "xem một sổ" (`packages/domain/src/ledger.ts` khai
 * hai tập cố ý khác nhau đúng phần tử `FUND_TRANSFER_IN`). Nếu sau này feed có thêm sự kiện
 * chuyển tiền liên sổ, nó **KHÔNG** thuộc `income` — tiền chỉ dời chỗ trong cùng nhóm.
 */
export const ACTIVITY_EVENT_KIND: Record<ActivityEventType, ActivityKind | "neutral"> = {
  [ACTIVITY_EVENT_TYPE.COLLECTION_CREATED]: "neutral",
  [ACTIVITY_EVENT_TYPE.EXPENSE_APPROVED]: ACTIVITY_KIND.EXPENSE,
  [ACTIVITY_EVENT_TYPE.EXPENSE_REJECTED]: "neutral",
  [ACTIVITY_EVENT_TYPE.MEMBER_JOINED]: "neutral",
  [ACTIVITY_EVENT_TYPE.MEMBER_LEFT]: "neutral",
  [ACTIVITY_EVENT_TYPE.PAYMENT_CONFIRMED]: ACTIVITY_KIND.INCOME,
  // 🔴 `neutral`, KHÔNG `income` — cùng lý lẽ với `collection.created` ngay trên: tiền CHƯA vào quỹ.
  // Lời khai là trạng thái (2); xếp nó vào "thu" là để chip lọc "Thu" đếm cả tiền chưa ai xác nhận,
  // tức đúng định nghĩa sổ ảo mà §3.7 cấm. Duyệt xong nó thành `payment.confirmed` và MỚI là `income`.
  [ACTIVITY_EVENT_TYPE.PAYMENT_AWAITING_CONFIRMATION]: "neutral",
  // US-E6.F3-0912 — lời khai bị từ chối: không đồng nào vào quỹ.
  [ACTIVITY_EVENT_TYPE.PAYMENT_CLAIM_REJECTED]: "neutral",
  // 🔴 `income` THẬT: khác lời khai ngay trên, tiền ĐÃ vào quỹ và đã có bút toán (DEBIT CASH). Chip
  // "Thu" đếm nó là đúng — bỏ ra ngoài mới là để tổng thu trên feed thiếu một khoản có thật.
  [ACTIVITY_EVENT_TYPE.INCOME_RECORDED]: ACTIVITY_KIND.INCOME,
  // 🔴 `neutral`, KHÔNG `expense` — cùng lý lẽ với lời khai chờ duyệt: tiền CHƯA rời quỹ. Chip "Chi" đếm nó là đếm khoản chi
  // chưa xảy ra. Duyệt xong nó thành `expense.approved` và MỚI là `expense`.
  [ACTIVITY_EVENT_TYPE.EXPENSE_AWAITING_APPROVAL]: "neutral",
};

/**
 * 🔴 QĐ người dùng 12/10/2026 (@UC0074 @UC0052) — người XEM làm được gì với dòng `expense.awaiting_approval`. MỘT định nghĩa với chi tiết
 * khoản chi (`expenseViewerActionsSchema` — xem docblock ở `expenses.ts`); đây chỉ là tên gọi phía feed, KHÔNG bản sao thứ hai.
 */
export const activityViewerActionsSchema = expenseViewerActionsSchema;
export type ActivityViewerActions = ExpenseViewerActions;

/**
 * 1 mục trong feed. `amountMinor` OMIT khi redacted (Member + fund private) HOẶC sự kiện không có tiền
 * (member.joined / member.left). `title` = mô tả ngắn (tên đợt thu / tên chi / tên thành viên).
 * `fundId` null với member.joined / member.left (không thuộc fund cụ thể).
 *
 * 🔴🔴 US-E24.F50-0502 (@UC0074) — `title` KHÔNG PHẢI ACTOR, đừng để client suy ra.
 * `title` đa hình theo nguồn (tên đợt thu · tên khoản chi · tên người nộp · tên người vào/rời nhóm),
 * nên từ nó không đọc ra được "ai làm việc này". `actorName`/`actorAvatarUrl` là field RIÊNG cho
 * NGƯỜI THỰC HIỆN, lấy từ **chủ thể nghiệp vụ** của từng loại sự kiện:
 *
 *  | eventType            | actor = ai                          | nguồn cột                                  |
 *  |----------------------|-------------------------------------|--------------------------------------------|
 *  | collection.created   | người MỞ đợt thu                    | `collections.createdBy` (worker ⇒ NULL)    |
 *  | expense.approved/rej | người LẬP khoản chi                 | `expenses.createdBy`                        |
 *  | payment.confirmed    | **người NỘP** (KHÔNG phải thủ quỹ)  | `member_dues → group_members` / `pay_on_behalf.payerMemberId` |
 *  | member.joined/left   | chính thành viên đó                 | `group_members`                             |
 *
 * 🔴 `payment.confirmed` là ca zero-tolerance: `/record-paid` chỉ O/A/T gọi được nên
 * `ledger_transactions.createdBy` là **THỦ QUỸ**. Lấy actor từ đó ⇒ feed cả nhóm đọc thấy *"Thủ quỹ
 * đã đóng quỹ 200.000đ"* cho khoản của người khác — đúng lớp bug đắt nhất repo
 * (CLAUDE.md §3.6 `expenses.payerMemberId`: *"suy chủ thể từ actor đang bấm nút là SAI khi hành
 * động cần quyền cao hơn người trong cuộc"*).
 *
 * 🔴 `actorName = null` có ĐÚNG HAI nghĩa và client phân biệt bằng ngữ cảnh, KHÔNG bằng chuỗi rỗng:
 * (a) việc do HỆ THỐNG chạy (worker sinh đợt thu ⇒ `createdBy` NULL) — client hiện "Hệ thống";
 * (b) danh tính bị ẩn theo `payment_status_visibility` / quỹ private. `''` là "có tên mà rỗng" ⇒
 * cấm (mirror lý do `amountMinor` bị OMIT chứ không hạ về `"0"` — số 0 là nói dối về tiền).
 */
export const activityItemSchema = z
  .object({
    /**
     * Khoá của HÀNG NGUỒN. Với `payment.awaiting_confirmation` đây là **`confirmationId`** — chính
     * id mà `POST /v1/payment-confirmations/:id/{approve,decline}` nhận, nên client không phải tra
     * thêm lần nào để hành động.
     *
     * 🔴 US-E8.F1-0871 (@UC9592) — dòng đã GIẤU NGƯỜI (`title`/`actorName` null theo
     * `payment_status_visibility`, gồm cả `member.joined`/`member.left` với member thường) mang id **MỜ**:
     * UUID v8 tất định, HMAC có khoá — không phải khoá nguồn, không tính ngược được, không mở được đường
     * đọc nào theo id bút toán. `nextCursor` cũng chỉ mang id mờ, và cursor tự dựng không thành oracle so
     * sánh (server chỉ nhận id trỏ đúng một dòng của bucket). Chỉ member thường gặp dòng như vậy (O/A/T không
     * bao giờ bị giấu người); `sourceRefHidden: true` báo cho client biết.
     * 🔄 US-E7.F1-0911 (E3, QĐ người dùng 07/10/2026): `member.joined`/`member.left` hiện người khi MỌI quỹ của nhóm MỞ; nhóm có
     * quỹ kín ⇒ vẫn giấu như trước.
     * ⚠️ Phạm vi: chỉ riêng feed. Các bề mặt khác ở cùng cấu hình — xem index §6 US-E8.F1-0871.
     */
    id: z.string().uuid(),
    eventType: activityEventTypeSchema,
    fundId: z.string().uuid().nullable(),
    /**
     * 🔴 US-E6.F3-0599 (@UC0051) — chỉ có giá trị với `payment.awaiting_confirmation`; `null` ở mọi
     * loại khác.
     *
     * Màn chi tiết đọc lời khai bằng `GET /v1/member-dues/{memberDueId}/confirmations`
     * (`ConfirmationReadService`, đã có từ US-E24.F9-0358) — nó trả `note` + URL bằng chứng TTL 15'.
     * Không mang khoá này thì client hoặc phải ĐOÁN, hoặc repo phải mọc endpoint thứ hai cho đúng dữ
     * liệu đã có đường đọc — cả hai đều là "cơ chế thứ hai" mà `CLAUDE.md §3.8` cấm.
     *
     * 🔴 US-E8.F1-0871 — dòng đã giấu người ⇒ LUÔN `null` (khoá khoản của một người cụ thể).
     */
    memberDueId: z.string().uuid().nullable(),
    title: z.string().nullable(),
    /**
     * 🔴 US-E24.F50-0793 (QĐ người dùng 15/09/2026) — NỘP CHO ĐỢT THU NÀO.
     *
     * `title` của `payment.confirmed` / `payment.awaiting_confirmation` là TÊN NGƯỜI NỘP (bảng trên),
     * nên dòng feed đọc thành *"Long đã nộp quỹ"* mà không nói nộp cho đợt nào. Hai field này mang
     * ĐỢT THU của khoản: `collectionTitle` = `collections.title` (null khi đợt chưa đặt tên — KHÔNG
     * bịa), `collectionPeriodIndex` = "kỳ thứ mấy của lịch" (`loadPeriodIndexes`, cùng phép xếp
     * hạng với màn tiến độ / sheet Nộp quỹ; null với đợt lẻ). Client ghép câu + nhãn kỳ qua ARB.
     * `collection.created` cũng mang chúng (title là tên đợt); mọi loại khác ⇒ null.
     *
     * 🔴 REDACTION: trên dòng CÁ NHÂN (nộp/khai) hai field này đi CÙNG cổng với `actorName` — bị ẩn
     * danh tính/tiền ⇒ `null` (đợt whitelist vài người + "(ẩn tên) đã nộp" là lộ đúng thứ
     * `managers_only` che). Dòng `collection.created` là chuyện cả nhóm ⇒ không ẩn.
     *
     * Với `income.recorded` (đóng góp thêm) ⇒ null: khoản đó không thuộc đợt nào, client hiện
     * *"Quỹ chung <tên nhóm>"* từ tên nhóm HIỆN TẠI (đổi tên nhóm ⇒ đổi theo; server không lưu chữ).
     */
    collectionTitle: z.string().nullable(),
    collectionPeriodIndex: z.number().int().positive().nullable(),
    /** Tên NGƯỜI THỰC HIỆN. null = hệ thống chạy HOẶC danh tính bị ẩn (xem docblock trên). */
    actorName: z.string().nullable(),
    /** Ảnh đại diện người thực hiện — **URL đã resolve**, không phải object key. */
    actorAvatarUrl: z.string().nullable(),
    /**
     * "Chi cho việc gì" — `expenses.description`. null với mọi eventType khác, và **null khi khoản
     * chi không `public`**: `schema.prisma` khai `Expense.visibility` chi phối CẢ số tiền LẪN
     * `description` ("ghi chú nhạy cảm — repo không có bảng note riêng"), mà feed là bề mặt CÔNG
     * KHAI của cả nhóm. Vắng mô tả ⇒ null, KHÔNG chuỗi rỗng.
     */
    description: z.string().nullable(),
    /** Số tiền của sự kiện (STRING minor unit, B1) — OMIT khi redacted / sự kiện không có tiền. */
    amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN).optional(),
    currency: z.string().length(3).nullable(),
    createdAt: z.string().datetime(),
    /**
     * 🔴 US-E7.F1-0911 (@UC9592 · QĐ người dùng 06/10/2026) — `true` ⇔ dòng đã GIẤU khoá nguồn: `id` là id MỜ và
     * `memberDueId` là `null` ⇒ không đường đọc chi tiết nào (nhãn bút toán, lời khai, khoản chi) nhận được nó. Client
     * dựa vào cờ này để quyết định member có mở màn chi tiết CHỈ-ĐỌC không — KHÔNG đoán từ `title`/`actorName` null (null
     * còn nghĩa "hệ thống chạy"). Luôn `false` với O/A/T và Sổ quỹ admin. Bắt buộc có mặt (`.strict()`, không optional).
     */
    sourceRefHidden: z.boolean(),
    /**
     * 🔴 US-E6.F3-0912 (@UC0051) — LÝ DO từ chối của dòng `payment.claim_rejected` (chữ người duyệt gõ). `null` ở mọi loại khác,
     * và `null` khi danh tính của dòng bị che (lý do có thể nêu tên người). Không bao giờ chuỗi rỗng.
     */
    rejectReason: z.string().min(1).nullable(),
    /**
     * 🔴 QĐ người dùng 12/10/2026 (@UC0074) — hành động người xem làm được với dòng này (`activityViewerActionsSchema`). Object ở
     * dòng `expense.awaiting_approval`; `null` ở MỌI loại khác (và ở Sổ quỹ admin — thấy ≠ duyệt được). Bắt buộc có mặt.
     */
    viewerActions: activityViewerActionsSchema.nullable(),
  })
  .strict();

export type ActivityItem = z.infer<typeof activityItemSchema>;

/** Response feed hoạt động nhóm — cursor keyset. `redacted`=true khi Member bị ẩn số tiền nào đó. */
export const activityFeedResponseSchema = z
  .object({
    data: z.array(activityItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
    redacted: z.boolean(),
  })
  .strict();

export type ActivityFeedResponse = z.infer<typeof activityFeedResponseSchema>;

/**
 * Response action-items (badge + card enrich). `totalCount` = tổng số việc cần làm. Mỗi item mang
 * ĐỦ dữ liệu render card mà KHÔNG cần call thứ 2 (fundName/collectionTitle/amount/dueDate/deadline).
 * Không việc → totalCount=0 + mảng rỗng + history tổng "0".
 *
 * Enrich (S25 · @UC0074): field mở rộng chỉ từ COLUMN thật (schema.prisma) — KHÔNG bịa:
 *  - dues/payOnBehalf: fundName (Fund.name), collectionTitle (Collection.title|null),
 *    amountMinor (MemberDue.amountMinor BigInt→string), currency, dueDate (Collection.dueDate|null),
 *    daysLeft (derive server ceil((dueDate-now)/86400000)|null khi dueDate null).
 *  - openVotes: expenseTitle (Expense.name|null), amountMinor (Expense.amountMinor→string), currency,
 *    deadline (Consensus.deadline|null — null hợp lệ khi mechanism=admin_only, KHÔNG gán lên Expense).
 *  - history: reuse tổng paid/owed/waived (BigInt→string) + recent dues đã đóng/miễn.
 */
export const actionItemsResponseSchema = z
  .object({
    totalCount: z.number().int().nonnegative(),
    /**
     * Dues của tôi CÒN NỢ — enrich đủ để render card nộp phí.
     *
     * 🔴 `partially_paid` THÊM 02/09/2026. Trước đó danh sách chỉ có `{pending, overdue}`, nên người
     * đã đóng 200/500 **biến mất khỏi màn Việc của tôi** — họ tin là xong, và hệ thống nhắc hạn
     * cũng bỏ sót họ (cùng hố ở `reminder.job`). Phần còn thiếu vẫn là nợ.
     *
     * `awaiting_confirmation` CỐ Ý không có: người đã bấm "tôi đã chuyển" đã làm phần của họ.
     */
    dues: z.array(
      z
        .object({
          memberDueId: z.string().uuid(),
          collectionId: z.string().uuid(),
          status: z.enum(["pending", "overdue", "partially_paid"]),
          fundName: z.string(),
          collectionTitle: z.string().nullable(),
          /** NGHĨA VỤ của suất — số gốc, KHÔNG trừ phần đã đóng. */
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          /**
           * 🔴 CÒN THIẾU — SERVER tính (`collection-progress.math.outstandingMinorOf`), THÊM
           * 02/09/2026 cùng lúc với `partially_paid` (vá review PR #680 vòng 2, H-3).
           *
           * Thiếu field này thì việc đưa `partially_paid` vào danh sách lại đẻ ra một lời nói dối
           * mới: thẻ của người đã đóng 200/500 hiện **500.000đ**, đúng cái sai mà PR này vừa vá ở
           * `reminder.job` (*"nói nghĩa vụ cho người đã trả một phần là đòi lại tiền họ đã trả"*).
           *
           * Client TUYỆT ĐỐI không tự trừ `amountMinor - paid` (`CLAUDE.md` §4): phép đó sai ở
           * `awaiting_confirmation` (còn thiếu NGUYÊN khoản — §3.7 đã khai ≠ đã nhận) và `waived`.
           * Với ba status của danh sách này hai vế trùng nhau, nhưng nhân bản phép tính tiền ra
           * client là cách nó lệch ở lần nới status kế tiếp.
           */
          outstandingMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          currency: z.string().length(3),
          dueDate: z.string().datetime().nullable(),
          daysLeft: z.number().int().nullable(),
          /**
           * 🔴 US-AIV2-0110 S30 (@UC0282) — khoản này của NHÓM nào và SUẤT nào. THÊM (additive, tuỳ chọn ở contract để client
           * cũ và fixture cũ không vỡ; server LUÔN điền). Vì sao cần: phụ huynh hai con cùng đợt nghe *"Thu quỹ tháng 9:
           * 111.000 đ; Thu quỹ tháng 9: 222.000 đ"* — hai dòng không phân biệt được; người ở hai nhóm không biết khoản nào của nhóm nào.
           *
           * `seatName` = tên SUẤT theo `resolveSeatDisplayName` (nhóm phụ huynh: tên trẻ, KHÔNG mượn tên phụ huynh; `null` =
           * suất chưa đặt tên — không bịa). Đây là suất CHÍNH người hỏi đại diện, nên không có luật che nào áp ở đây.
           */
          groupId: z.string().uuid().optional(),
          groupName: z.string().optional(),
          groupMemberId: z.string().uuid().optional(),
          seatName: z.string().nullable().optional(),
        })
        .strict(),
    ),
    /** Phiên vote đang mở tôi CHƯA bỏ phiếu — enrich tiêu đề/số tiền/hạn để render card vote. */
    openVotes: z.array(
      z
        .object({
          expenseId: z.string().uuid(),
          consensusId: z.string().uuid(),
          expenseTitle: z.string().nullable(),
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          currency: z.string().length(3),
          deadline: z.string().datetime().nullable(),
        })
        .strict(),
    ),
    /** Đề nghị nộp-hộ chờ tôi (beneficiary) respond — mirror dues enrich (fund/tên đợt/số tiền). */
    payOnBehalf: z.array(
      z
        .object({
          payOnBehalfId: z.string().uuid(),
          memberDueId: z.string().uuid(),
          fundName: z.string(),
          collectionTitle: z.string().nullable(),
          /**
           * 🔴 POB-ACCEPT-CLOSED-STATES (H1 review #1177) — PHẦN CÒN THIẾU của suất (server tính, `remainingMinorOf`), ĐÚNG số lõi ghi
           * vào sổ và ghi nợ khi B "Đồng ý" — không phải nghĩa vụ gốc. Suất không còn nhận nộp hộ (đã đóng · được miễn · đang chờ xác
           * nhận) thì đề nghị không có trong danh sách. Client hiển thị nguyên giá trị, không tự trừ.
           */
          amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
          /**
           * 🔴 G-3 GUIDE-POB-ANSWER-LOOP (@UC0371) — SUẤT được đóng hộ (mirror `dues.groupMemberId`/`seatName` của S30). THÊM, tuỳ chọn ở contract
           * (client cũ/fixture cũ không vỡ; server LUÔN điền). Vì sao cần: bà đề nghị đóng hộ CẢ HAI bé cùng đợt ⇒ thẻ trợ lý nói «Quỹ lớp tháng
           * 10, Quỹ lớp tháng 10» — hai mục không phân biệt được. `seatName` = cùng luật tên với `dues` (`null` = suất chưa đặt tên — không bịa).
           */
          groupMemberId: z.string().uuid().optional(),
          seatName: z.string().nullable().optional(),
        })
        .strict(),
    ),
    /** Lịch sử đóng của tôi (paid/paid_on_behalf/waived) — tổng string + recent rows. */
    history: z
      .object({
        totalPaidMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        totalOwedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        totalWaivedMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
        recent: z.array(
          z
            .object({
              memberDueId: z.string().uuid(),
              collectionTitle: z.string().nullable(),
              amountMinor: z.string().regex(MONEY_INTEGER_STRING_PATTERN),
              status: z.string(),
              dueDate: z.string().datetime().nullable(),
            })
            .strict(),
        ),
      })
      .strict(),
  })
  .strict();

export type ActionItemsResponse = z.infer<typeof actionItemsResponseSchema>;
